import { useCallback, useEffect, useRef } from "react"

import {
  testSetEditorEventBus,
  type EventListener,
  type EventPayloads,
  type EventType,
} from "../utils/eventBus"

/**
 * Custom hook for subscribing to event bus events
 * Automatically handles cleanup when component unmounts
 */
export function useEventBus<T extends EventType>(
  eventType: T,
  listener: EventListener<T>,
): void {
  const listenerRef = useRef(listener)

  useEffect(() => {
    listenerRef.current = listener
  }, [listener])

  useEffect(() => {
    const unsubscribe = testSetEditorEventBus.subscribe(eventType, ((
      payload: EventPayloads[T],
    ) => listenerRef.current(payload)) as EventListener<T>)
    return unsubscribe
  }, [eventType])
}

/**
 * Custom hook for dispatching events
 * Returns a memoized dispatch function
 */
export function useEventDispatcher() {
  return useCallback(
    <T extends EventType>(eventType: T, payload: EventPayloads[T]) => {
      testSetEditorEventBus.dispatch(eventType, payload)
    },
    [],
  )
}
